// «الجرد الشهري» — small shared helpers for every stock-count screen.
import { toLatinDigits } from "@/lib/wmDigits";

export {
    WM_TIMEOUT_MS as SC_TIMEOUT_MS, wmError as scError, wmCode as scCode,
    wmMessage as scMessage, wmLineErrors as scLineErrors,
} from "@/lib/wmError";
export { fmt, labelOf, fmtDateTime } from "@/components/cancellations/coUtils";

/** Export / upload calls get a long timeout; the server parses ERP files in a thread. */
export const SC_LONG_TIMEOUT_MS = 180000;

const nf = (lang, digits) =>
    new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US", { minimumFractionDigits: 0, maximumFractionDigits: digits });

/** SAR amount (a float from the API, already in riyals) -> "1,234.50 ر.س". */
export function fmtMoney(sar, lang) {
    if (sar === null || sar === undefined || sar === "") return "";
    const n = Number(sar);
    if (Number.isNaN(n)) return "";
    const s = new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US", {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(n);
    return `${s} ${lang === "ar" ? "ر.س" : "SAR"}`;
}

/** Unit cost (float, up to 4 decimals). */
export function fmtCost(sar, lang) {
    if (sar === null || sar === undefined || sar === "") return "";
    return nf(lang, 4).format(Number(sar));
}

/** Quantity (float, up to 3 decimals). 0 renders as 0, null as "". */
export function fmtQuantity(q, lang) {
    if (q === null || q === undefined || q === "") return "";
    return nf(lang, 3).format(Number(q));
}

/** A typed quantity: digits + ONE decimal point («٫» or «,» accepted as the point). */
export function sanitizeQuantity(raw, allowsDecimal = true) {
    let s = toLatinDigits(String(raw ?? "").replace(/[٫,]/g, ".")).replace(/[^\d.]/g, "");
    const i = s.indexOf(".");
    if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "").slice(0, 3);
    if (!allowsDecimal) s = s.split(".")[0];
    return s;
}

/** "0" IS a count; only empty / null / undefined is "not counted". */
export const isCounted = (v) => v !== "" && v !== undefined && v !== null;

/** "2026-10" -> «أكتوبر ٢٠٢٦» / "October 2026". */
export function fmtPeriod(period, lang) {
    if (!period) return "";
    const [y, m] = period.split("-").map(Number);
    try {
        return new Intl.DateTimeFormat(lang === "ar" ? "ar-SA" : "en-US", {
            year: "numeric", month: "long", calendar: "gregory", timeZone: "UTC",
        }).format(new Date(Date.UTC(y, m - 1, 15)));
    } catch {
        return period;
    }
}

/** "2026-11-02" -> «٢ نوفمبر» / "2 Nov". */
export function fmtDay(iso, lang) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-").map(Number);
    try {
        return new Intl.DateTimeFormat(lang === "ar" ? "ar-SA" : "en-GB", {
            day: "numeric", month: "long", calendar: "gregory", timeZone: "UTC",
        }).format(new Date(Date.UTC(y, m - 1, d)));
    } catch {
        return iso;
    }
}

/** Read a server error from a blob response (export download). */
export async function blobErrorMessage(e, lang) {
    try {
        const txt = await e?.response?.data?.text?.();
        const d = JSON.parse(txt || "{}").detail;
        if (d && typeof d === "object") return lang === "ar" ? d.message_ar || d.message_en : d.message_en || d.message_ar;
    } catch {
        /* not JSON */
    }
    return null;
}

/** Status chip tone per sc status key. */
export const STATUS_TONE = {
    not_started: "bg-[color:var(--bg-surface-2)] text-[color:var(--ink-muted)]",
    draft: "bg-[color:var(--warning-soft)] text-[color:var(--warning-soft-fg)]",
    submitted: "bg-[color:var(--brand-orange-soft)] text-[color:var(--brand-orange-soft-fg)]",
    approved: "bg-[color:var(--success-soft)] text-[color:var(--success-soft-fg)]",
};
